/**
 * vocab.ts — Etichette e ordine canonico di Periodo / Movimento / Genere.
 *
 * `label_en` qui vale come etichetta in SPAGNOLO (lingua della materia):
 * il campo conserva il nome del golden per non divergere dal tipo condiviso.
 *
 * The TYPE-level closed unions live in schema.ts (so the compiler catches
 * typos in author JSONs). This file ships the human-readable labels (EN + IT)
 * and the order in which filter UIs render values.
 *
 * Adding a new vocab value: add it to BOTH the union in schema.ts AND the
 * entry here. The render layer reads from here exclusively.
 */

import type { Period, Movement, Genre, ThemeId } from './schema';

export interface VocabEntry<T extends string> {
  id: T;
  label_en: string;
  label_it: string;
  order: number;
}

// ══════ PERIODI (cronologico; l'Ispanoamerica chiude, non si interseca) ══════

export const PERIOD_VOCAB: ReadonlyArray<VocabEntry<Period>> = [
  { id: 'edad-media',           label_en: 'Edad Media',              label_it: 'Medioevo',                    order: 1 },
  { id: 'prerrenacimiento',     label_en: 'Prerrenacimiento',        label_it: 'Prerinascimento',             order: 2 },
  { id: 'renacimiento',         label_en: 'Renacimiento',            label_it: 'Rinascimento',                order: 3 },
  { id: 'barroco',              label_en: 'Barroco',                 label_it: 'Barocco',                     order: 4 },
  { id: 'ilustracion',          label_en: 'Ilustracion',             label_it: 'Illuminismo',                 order: 5 },
  { id: 'romanticismo',         label_en: 'Romanticismo',            label_it: 'Romanticismo',                order: 6 },
  { id: 'realismo-naturalismo', label_en: 'Realismo y Naturalismo',  label_it: 'Realismo e Naturalismo',      order: 7 },
  { id: 'fin-de-siglo',         label_en: 'Fin de siglo',            label_it: 'Fine secolo (Modernismo e \'98)', order: 8 },
  { id: 'vanguardias',          label_en: 'Vanguardias y 27',        label_it: 'Avanguardie e Generazione del 27', order: 9 },
  { id: 'posguerra',            label_en: 'Posguerra',               label_it: 'Dopoguerra',                  order: 10 },
  { id: 'contemporaneo',        label_en: 'Contemporaneo',           label_it: 'Contemporaneo',               order: 11 },
  { id: 'hispanoamerica',       label_en: 'Hispanoamerica',          label_it: 'Ispanoamerica',               order: 12 },
];

// ══════ MOVIMENTI ══════

export const MOVEMENT_VOCAB: ReadonlyArray<VocabEntry<Movement>> = [
  { id: 'mester-de-juglaria',   label_en: 'Mester de juglaria',   label_it: 'Mester de juglaria',      order: 1 },
  { id: 'mester-de-clerecia',   label_en: 'Mester de clerecia',   label_it: 'Mester de clerecia',      order: 2 },
  { id: 'humanismo',            label_en: 'Humanismo',            label_it: 'Umanesimo',               order: 3 },
  { id: 'mistica',              label_en: 'Mistica',              label_it: 'Mistica',                 order: 4 },
  { id: 'picaresca',            label_en: 'Picaresca',            label_it: 'Picaresca',               order: 5 },
  { id: 'culteranismo',         label_en: 'Culteranismo',         label_it: 'Culteranismo',            order: 6 },
  { id: 'conceptismo',          label_en: 'Conceptismo',          label_it: 'Concettismo',             order: 7 },
  { id: 'neoclasicismo',        label_en: 'Neoclasicismo',        label_it: 'Neoclassicismo',          order: 8 },
  { id: 'romanticismo-mov',     label_en: 'Romanticismo',         label_it: 'Romanticismo',            order: 9 },
  { id: 'realismo',             label_en: 'Realismo',             label_it: 'Realismo',                order: 10 },
  { id: 'naturalismo',          label_en: 'Naturalismo',          label_it: 'Naturalismo',             order: 11 },
  { id: 'modernismo',           label_en: 'Modernismo',           label_it: 'Modernismo',              order: 12 },
  { id: 'generacion-98',        label_en: 'Generacion del 98',    label_it: 'Generazione del \'98',    order: 13 },
  { id: 'novecentismo',         label_en: 'Novecentismo',         label_it: 'Novecentismo',            order: 14 },
  { id: 'generacion-27',        label_en: 'Generacion del 27',    label_it: 'Generazione del \'27',    order: 15 },
  { id: 'esperpento',           label_en: 'Esperpento',           label_it: 'Esperpento',              order: 16 },
  { id: 'tremendismo',          label_en: 'Tremendismo',          label_it: 'Tremendismo',             order: 17 },
  { id: 'existencialismo',      label_en: 'Existencialismo',      label_it: 'Esistenzialismo',         order: 18 },
  { id: 'realismo-magico',      label_en: 'Realismo magico',      label_it: 'Realismo magico',         order: 19 },
  { id: 'literatura-fantastica',label_en: 'Literatura fantastica',label_it: 'Letteratura fantastica',  order: 20 },
  { id: 'memoria-historica',    label_en: 'Memoria historica',    label_it: 'Memoria storica',         order: 21 },
];

// ══════ GENERI / FORME ══════

export const GENRE_VOCAB: ReadonlyArray<VocabEntry<Genre>> = [
  // Poesia
  { id: 'cantar-de-gesta',  label_en: 'Cantar de gesta',  label_it: 'Cantare di gesta',   order: 1 },
  { id: 'poesia-lirica',    label_en: 'Poesia lirica',    label_it: 'Poesia lirica',      order: 2 },
  { id: 'soneto',           label_en: 'Soneto',           label_it: 'Sonetto',            order: 3 },
  { id: 'egloga',           label_en: 'Egloga',           label_it: 'Egloga',             order: 4 },
  { id: 'romance',          label_en: 'Romance',          label_it: 'Romance',            order: 5 },
  { id: 'poesia-mistica',   label_en: 'Poesia mistica',   label_it: 'Poesia mistica',     order: 6 },
  // Prosa narrativa
  { id: 'novela',           label_en: 'Novela',           label_it: 'Romanzo',            order: 7 },
  { id: 'novela-picaresca', label_en: 'Novela picaresca', label_it: 'Romanzo picaresco',  order: 8 },
  { id: 'novela-corta',     label_en: 'Novela corta',     label_it: 'Romanzo breve',      order: 9 },
  { id: 'nivola',           label_en: 'Nivola',           label_it: 'Nivola',             order: 10 },
  { id: 'cuento',           label_en: 'Cuento',           label_it: 'Racconto',           order: 11 },
  { id: 'leyenda',          label_en: 'Leyenda',          label_it: 'Leggenda',           order: 12 },
  // Teatro
  { id: 'comedia-nueva',    label_en: 'Comedia nueva',    label_it: 'Commedia nuova',     order: 13 },
  { id: 'tragedia',         label_en: 'Tragedia',         label_it: 'Tragedia',           order: 14 },
  { id: 'drama',            label_en: 'Drama',            label_it: 'Dramma',             order: 15 },
  { id: 'auto-sacramental', label_en: 'Auto sacramental', label_it: 'Auto sacramentale',  order: 16 },
  { id: 'esperpento-gen',   label_en: 'Esperpento',       label_it: 'Esperpento',         order: 17 },
  // Non-fiction
  { id: 'ensayo',           label_en: 'Ensayo',           label_it: 'Saggio',             order: 18 },
  { id: 'epistolar',        label_en: 'Epistolar',        label_it: 'Epistolare',         order: 19 },
  { id: 'cronica',          label_en: 'Cronica',          label_it: 'Cronaca',            order: 20 },
];

// ══════ Lookup helpers ══════

const PERIOD_BY_ID = new Map(PERIOD_VOCAB.map(e => [e.id, e]));
const MOVEMENT_BY_ID = new Map(MOVEMENT_VOCAB.map(e => [e.id, e]));
const GENRE_BY_ID = new Map(GENRE_VOCAB.map(e => [e.id, e]));

/** Lingue dell'interfaccia. 'es' = lingua della materia (etichette in
 *  `label_en`, campo tenuto col nome del golden per non divergere dal tipo). */
export type UiLocale = 'es' | 'it';

export function periodLabel(id: Period, locale: UiLocale): string {
  const e = PERIOD_BY_ID.get(id);
  return e ? (locale === 'es' ? e.label_en : e.label_it) : id;
}

export function movementLabel(id: Movement, locale: UiLocale): string {
  const e = MOVEMENT_BY_ID.get(id);
  return e ? (locale === 'es' ? e.label_en : e.label_it) : id;
}

export function genreLabel(id: Genre, locale: UiLocale): string {
  const e = GENRE_BY_ID.get(id);
  return e ? (locale === 'es' ? e.label_en : e.label_it) : id;
}

/** Theme id is Movement | Genre — resolves in either vocab. */
export function themeLabel(id: ThemeId, locale: UiLocale): string {
  const mv = MOVEMENT_BY_ID.get(id as Movement);
  if (mv) return locale === 'es' ? mv.label_en : mv.label_it;
  const gn = GENRE_BY_ID.get(id as Genre);
  if (gn) return locale === 'es' ? gn.label_en : gn.label_it;
  return id;
}

/** "movement" | "genre" | "unknown" — useful for the ThemeMapView lens. */
export function themeKind(id: ThemeId): 'movement' | 'genre' | 'unknown' {
  if (MOVEMENT_BY_ID.has(id as Movement)) return 'movement';
  if (GENRE_BY_ID.has(id as Genre)) return 'genre';
  return 'unknown';
}

/**
 * Etichetta localizzata del TIPO di un nodo mappa (opera / movimento / genere /
 * tema). Usata come caption nei nodi della mappa autore e della mappa temi:
 * prima erano stringhe inglesi hardcoded ("Work ▶", "GENRE") anche con UI in
 * italiano. 'unknown' ricade su "tema".
 */
const NODE_KIND_LABELS: Record<string, { it: string; en: string }> = {
  work:     { it: 'Opera',     en: 'Work' },
  movement: { it: 'Movimento', en: 'Movement' },
  genre:    { it: 'Genere',    en: 'Genre' },
  theme:    { it: 'Tema',      en: 'Theme' },
  unknown:  { it: 'Tema',      en: 'Theme' },
};

export function nodeKindLabel(kind: string, locale: UiLocale): string {
  const e = NODE_KIND_LABELS[kind];
  return e ? (locale === 'es' ? e.en : e.it) : kind;
}
